<template>
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
        <div class="flex items-start justify-between gap-4 mb-4">
            <div>
                <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Import Artist
                </h2>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                    Paste a Cifra Club artist URL or type a name to search and import data from the chords service.
                </p>
            </div>
        </div>

        <form @submit.prevent="onSubmit" class="space-y-4">
            <div class="flex flex-col sm:flex-row gap-3">
                <input
                    v-model="query"
                    type="text"
                    :disabled="loading"
                    class="flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Type an artist name or paste the URL…"
                />
                <button
                    type="submit"
                    :disabled="loading"
                    class="flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    <svg
                        v-if="loading"
                        class="animate-spin w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        />
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                    </svg>
                    <svg
                        v-else
                        class="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1116.65 5.4a7.5 7.5 0 010 11.3z"
                        />
                    </svg>
                    <span>{{ loading ? 'Searching…' : 'Import Artist' }}</span>
                </button>
            </div>
        </form>

        <div v-if="helperMessage" class="mt-3">
            <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ helperMessage }}
            </p>
        </div>

        <div v-if="errorMessage" class="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-sm text-red-700 dark:text-red-200">
            {{ errorMessage }}
        </div>

        <div v-if="result" class="mt-4">
            <div class="p-4 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 mb-3">
                <p class="text-sm font-medium text-green-900 dark:text-green-200">
                    Artist information retrieved successfully.
                </p>
            </div>
            <details class="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <summary class="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-200">
                    View raw response
                </summary>
                <pre class="mt-3 overflow-x-auto text-xs text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">{{ formattedResult }}</pre>
            </details>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from "vue";
import toastStore from "@/store/toast.store";

const query = ref("");
const loading = ref(false);
const result = ref(null);
const errorMessage = ref("");

const helperMessage = computed(() => {
    if (!query.value) {
        return "Example: https://www.cifraclub.com.br/artist-name/ or simply \"Artist Name\".";
    }
    return "";
});

const formattedResult = computed(() => {
    if (!result.value) {
        return "";
    }
    return JSON.stringify(result.value, null, 2);
});

const buildSearchParams = (input) => {
    const params = new URLSearchParams();
    params.append("q", input);
    params.append("url", input);
    return params;
};

const onSubmit = async () => {
    const trimmed = query.value.trim();

    if (!trimmed) {
        errorMessage.value = "Please enter an artist name or URL.";
        return;
    }

    loading.value = true;
    errorMessage.value = "";
    result.value = null;

    try {
        const response = await fetch(`/api/admin/artist/import?${buildSearchParams(trimmed).toString()}`);

        if (!response.ok) {
            let message = "Unable to import artist. Please check the name or URL and try again.";
            try {
                const payload = await response.json();
                if (payload?.error) {
                    message = payload.error;
                }
            } catch (e) {
                // ignore JSON parse errors
            }
            throw new Error(message);
        }

        const data = await response.json();
        result.value = data;
        toastStore.dispatch("addMessage", {
            message: "Artist data imported successfully.",
            icon: "success"
        });
    } catch (error) {
        errorMessage.value = error.message || "Unexpected error occurred while importing the artist.";
        toastStore.dispatch("addMessage", {
            message: "Failed to import artist.",
            icon: "error"
        });
    } finally {
        loading.value = false;
    }
};
</script>

